import { Button } from "@istari-frontend/ui";
import { CheckIcon, CircleCheckIcon, CircleXIcon, ShieldAlertIcon } from "lucide-react";
import { type CSSProperties, useState } from "react";

import { type ApprovalAction, resolveApproval } from "~/ai-chat/data-source/resolve-approval";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import type { ChatApproval } from "~/ai-chat/model/chat-message";

interface ChatApprovalCardProps {
  message: ChatApproval;
  // Approve = resume the paused turn: re-open the chat stream with the approval id so the server
  // runs the gated call and streams the continuation. Supplied by the transcript (from useChatSend).
  // Decline does NOT use this — it POSTs the decision to the approvals endpoint (resolveApproval).
  onApprove: (approvalId: string) => Promise<void>;
}

// A lost response AFTER an accept is NOT a decline: the server may already be running the action.
// Never claim "nothing ran" in that case. A failed decline is safe to call out as not-run.
const FAILURE_LABEL: Record<ApprovalAction, string> = {
  accept: "Couldn't confirm your approval — the action may or may not have started. Check before retrying.",
  decline: "Couldn't record your decision. Nothing ran.",
};

// The `--tone` the card is tinted with, from the lifecycle status. Only the border, header tint,
// shield icon, and outcome icon take the tone; all text stays foreground/muted (see the mockup).
function toneVar(status: ChatApproval["status"]): string {
  if (status === "accepted") return "var(--success)";
  if (status === "declined" || status === "failed") return "var(--destructive)";
  return "var(--warning)"; // pending | expired
}

// Pull the gated method names the server listed in its prompt (lines like "  - admin.users.list"),
// so the card shows the methods in a code box rather than the full prose. Falls back to the tool name.
function parseGatedMethods(message: string): string[] {
  return message
    .split("\n")
    .map((line) => /^\s*[-•]\s*(.+?)\s*$/.exec(line)?.[1])
    .filter((m): m is string => Boolean(m));
}

// Copy shown once the card is settled (non-pending). `failedAction` falls back to the card-local
// value when the item hasn't persisted one.
function outcomeLabel(message: ChatApproval, failedAction: ApprovalAction | null): string {
  switch (message.status) {
    case "accepted":
      return "Approved";
    case "declined":
      return "Denied";
    case "expired":
      return "Expired — not approved in time.";
    default:
      return FAILURE_LABEL[message.failedAction ?? failedAction ?? "accept"]; // failed
  }
}

/**
 * Inline card for a privileged (admin / access-control) tool call the chat stream paused on.
 * Approve resumes the turn (re-opens the stream); Deny POSTs the decision. Fail-closed: left
 * unanswered, the server times out and nothing runs.
 */
export function ChatApprovalCard({ message, onApprove }: ChatApprovalCardProps) {
  const { updateMessage } = useAiChat();
  const [submitting, setSubmitting] = useState(false);
  // Fallback for the failure copy before the persisted `message.failedAction` is read back.
  const [failedAction, setFailedAction] = useState<ApprovalAction | null>(null);

  const decide = async (action: ApprovalAction) => {
    if (submitting || message.status !== "pending") return;
    setSubmitting(true);
    // Optimistic: show the decision immediately and disable the buttons.
    const optimistic = action === "accept" ? "accepted" : "declined";
    updateMessage(message.id, (item) => (item.kind === "approval" ? { ...item, status: optimistic } : item));
    try {
      if (action === "accept") {
        // Approve resumes the turn: re-open the stream, which runs the gated call and streams the
        // continuation. Rejects (→ catch) if the approval can't be resumed (expired / already answered).
        await onApprove(message.approvalId);
      } else {
        await resolveApproval({ approvalId: message.approvalId, action });
      }
    } catch {
      setFailedAction(action);
      // Persist which action failed on the item (not just local state) so the right copy survives a remount.
      updateMessage(message.id, (item) =>
        item.kind === "approval" ? { ...item, status: "failed", failedAction: action } : item,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const methods = parseGatedMethods(message.message);
  const shown = methods.length > 0 ? methods : [message.tool];
  const count = shown.length;
  const lead =
    message.status === "pending"
      ? "The assistant wants to run this with your privileges:"
      : "The assistant asked to run this with your privileges:";

  // `--tone` drives the border / header tint / icons; the theme vars keep the card on-theme in
  // both light and dark. Cast because CSSProperties doesn't type custom properties.
  const cardStyle = {
    "--tone": toneVar(message.status),
    background: "var(--sidebar)",
    border: "1px solid color-mix(in oklab, var(--tone) 30%, transparent)",
  } as CSSProperties;
  const countStyle: CSSProperties = { color: "color-mix(in oklab, var(--foreground) 70%, transparent)" };
  const headerStyle: CSSProperties = { background: "color-mix(in oklab, var(--tone) 15%, transparent)" };
  const toneColor: CSSProperties = { color: "var(--tone)" };
  const methodsStyle: CSSProperties = { border: "1px solid var(--border)", background: "var(--background)" };

  return (
    <section
      aria-label="Approval required"
      style={cardStyle}
      className="flex w-full flex-col overflow-hidden rounded-lg"
    >
      <div className="flex items-center gap-2 px-4 py-2 text-xs font-medium" style={headerStyle}>
        <ShieldAlertIcon className="size-4 shrink-0" style={toneColor} />
        Approval required
        <span className="ml-auto font-normal" style={countStyle}>
          {count} privileged {count === 1 ? "method" : "methods"}
        </span>
      </div>
      <div className="flex flex-col px-4 py-3">
        <p className="text-muted-foreground m-0 text-[13px] leading-relaxed">{lead}</p>
        <ul className="m-0 mt-2 flex list-none flex-col gap-1 rounded-md px-2.5 py-2" style={methodsStyle}>
          {shown.map((m) => (
            <li key={m} className="font-mono text-xs leading-snug break-all">
              {m}
            </li>
          ))}
        </ul>
        {message.status === "pending" ? (
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              type="button"
              size="2xs"
              variant="ghost"
              onClick={() => void decide("decline")}
              disabled={submitting}
            >
              Deny
            </Button>
            <Button type="button" size="2xs" onClick={() => void decide("accept")} disabled={submitting}>
              <CheckIcon className="size-4" />
              Approve
            </Button>
          </div>
        ) : (
          <span className="mt-3 flex items-center gap-1.5 text-xs font-medium">
            {message.status === "accepted" ? (
              <CircleCheckIcon className="size-3.5 shrink-0" style={toneColor} />
            ) : (
              <CircleXIcon className="size-3.5 shrink-0" style={toneColor} />
            )}
            {outcomeLabel(message, failedAction)}
          </span>
        )}
      </div>
    </section>
  );
}
